import { DatePipe } from '@angular/common';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NgxPermissionsService } from 'ngx-permissions';
import { ConfirmationService, MessageService } from 'primeng/api';
import { lastValueFrom } from 'rxjs';
import { LiveControllerService, LiveDetail } from 'src/app/admin-api';
import {
  TableActionButton,
  TableColumn,
  TableComponent
} from 'src/app/components/table';
import { Role, roleAsObject } from 'src/app/models';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-lives',
  templateUrl: './lives.component.html',
  styleUrl: './lives.component.scss',
  providers: [MessageService, ConfirmationService, DatePipe]
})
export class LivesComponent implements OnInit {
  @ViewChild(TableComponent) table: TableComponent;

  liveService = inject(LiveControllerService);
  permissionService = inject(NgxPermissionsService);
  title = inject(Title);
  messageService = inject(MessageService);
  confirmationService = inject(ConfirmationService);
  datePipe = inject(DatePipe);

  cols: TableColumn[];
  permission: boolean;

  actionButtons: TableActionButton[] = [
    new TableActionButton.Builder()
      .setAction((live: LiveDetail) => this.deleteLive(live))
      .setLabel('')
      .setIcon('pi pi-trash')
      .setTooltip('Excluir Live')
      .setSeverity('danger')
      .setDisplay((live: LiveDetail) => this.permission && !live.active)
      .build()
  ];

  async ngOnInit(): Promise<void> {
    LoaderService.showLoader();
    this.title.setTitle('Conteúdo - Lives');
    try {
      this.permission = await this.permissionService.hasPermission([
        roleAsObject(Role.Full_Administrator).enumValue,
        roleAsObject(Role.Content).enumValue
      ]);
    } catch (error) {
      this.permission = false;
      AppDialogService.showErrorDialog(error);
    } finally {
      this.cols = this.columns;
    }
  }

  async updateLiveStatus(live: LiveDetail, stop = false) {
    if (!live.active && !stop) {
      this.confirmationService.confirm({
        header: 'Interromper a live atual?',
        acceptLabel: 'Interromper!',
        rejectLabel: 'Voltar',
        rejectButtonStyleClass: 'p-button-primary',
        acceptButtonStyleClass: 'p-button-danger',
        accept: async () => {
          await this.updateLiveStatus(live, true);
        },
        reject: () => (live.active = !live.active)
      });
      return;
    }

    try {
      LoaderService.showLoader();
      await lastValueFrom(this.liveService.updateLive(live, live.liveId));
    } catch (error) {
      live.active = !live.active;
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  get columns() {
    return [
      new TableColumn.Builder()
        .setHeader('Id')
        .setField('liveId')
        .setType('number')
        .setRouterLink('')
        .setRouterLinkFieldName('liveId')
        .build(),
      new TableColumn.Builder()
        .setHeader('Título')
        .setField('title')
        .setRouterLink('')
        .setRouterLinkFieldName('liveId')
        .setCondition('contains')
        .build(),
      new TableColumn.Builder()
        .setHeader('Cupom')
        .setField('couponName')
        .setCondition('contains')
        .setRouterLink('/marketing/coupons/')
        .setRouterLinkFieldName('couponId')
        .setLinkActive((live: LiveDetail) => live.couponId > 0)
        .build(),
      new TableColumn.Builder()
        .setHeader('Coleção')
        .setField('collectionId')
        .setRouterLink('/shop-products/categories/category/')
        .setRouterLinkFieldName('collectionId')
        .setLinkActive(
          (live: LiveDetail) => live.collectionId?.trim()?.length > 0
        )
        .build(),
      new TableColumn.Builder()
        .setHeader('AO VIVO')
        .setField('active')
        .setType(this.permission ? 'inputSwitch' : 'boolean')
        .build(),
      new TableColumn.Builder()
        .setHeader('Likes')
        .setField('likes')
        .setType('formattedNumber')
        .setCondition('gte')
        .build(),
      new TableColumn.Builder()
        .setHeader('Visualizações')
        .setField('views')
        .setType('formattedNumber')
        .setCondition('gte')
        .build(),
      new TableColumn.Builder()
        .setHeader('Publicação')
        .setField('datePublished')
        .setType('date')
        .setCondition('between')
        .setDisplayFunction((live: LiveDetail) =>
          this.datePipe.transform(live.datePublished, 'dd/MM/yyyy')
        )
        .build(),
      new TableColumn.Builder()
        .setHeader('Últ. atualização')
        .setField('')
        .setField('dateUpdated')
        .setType('date')
        .setCondition('between')
        .build(),
      new TableColumn.Builder()
        .setHeader('Ações')
        .setType('button')
        .setFilter(false)
        .setSortable(false)
        .build()
    ];
  }

  public deleteLive(live: LiveDetail) {
    if (this.permission) {
      this.confirmationService.confirm({
        header: 'Excluir Live',
        message: `Você tem certeza que deseja excluir a live ${live.title}?`,
        acceptLabel: 'Excluir',
        rejectLabel: 'Cancelar',
        acceptButtonStyleClass: 'p-button-danger',
        rejectButtonStyleClass: 'p-button-primary',
        accept: async () => {
          try {
            LoaderService.showLoader();
            await lastValueFrom(this.liveService.deleteLive(live.liveId));
            this.messageService.add({
              severity: 'success',
              summary: 'Sucesso',
              detail: `Live ${live.title} excluída com sucesso!`
            });
            await this.table.refresh();
          } catch (error) {
            AppDialogService.showErrorDialog(error);
          } finally {
            LoaderService.showLoader(false);
          }
        }
      });
    }
  }
}
